/**
 * BankConnectionItem
 * One linked Plaid bank connection row (#563): institution, status badge, last
 * synced time, and health actions. Mirrors the calendar `CalendarConnectionItem`
 * pattern, adapted to the Plaid lifecycle:
 *   - LoginRequired → an inline "needs re-authentication" prompt explaining why
 *     new transactions stopped, with the Reconnect (update-mode) action, which
 *     names the specific bank being re-authenticated.
 *   - Active        → a Disconnect action, styled destructive-at-rest so it reads
 *     as the connection-killing action, distinct from the repairing Reconnect.
 *   - Disconnected  → a terminal-state panel: the Item's token is revoked, so it
 *     can't be reconnected (update mode can't heal a removed Item). The only
 *     recovery is a fresh Re-link, offered here (ADR-070, ADR-075).
 */

import { Landmark, RefreshCw, Trash2, AlertTriangle, Loader2, Unplug } from 'lucide-react';
import { BankConnectionView, BankConnectionStatus } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { formatRelativeTime } from '@/lib/utils';
import { BankConnectionStatusBadge } from './BankConnectionStatusBadge';

interface BankConnectionItemProps {
  connection: BankConnectionView;
  onReconnect: () => void;
  onDisconnect: () => void;
  /** Start a fresh Link for a permanently-Disconnected bank (creates a new Plaid Item). */
  onRelink: () => void;
  /** On-demand "sync now" for an Active connection — the free cached pull. */
  onSync: () => void;
  isReconnecting?: boolean;
  isDisconnecting?: boolean;
  isRelinking?: boolean;
  isSyncing?: boolean;
}

export function BankConnectionItem({
  connection,
  onReconnect,
  onDisconnect,
  onRelink,
  onSync,
  isReconnecting = false,
  isDisconnecting = false,
  isRelinking = false,
  isSyncing = false,
}: BankConnectionItemProps) {
  // Reconnect only heals a LoginRequired Item (Plaid Link update mode re-auths the SAME
  // Item). A Disconnected Item's token is revoked, so it can't be reconnected — re-linking
  // creates a fresh Item. Disconnect only applies while the connection is still live.
  const showReconnect = connection.status === BankConnectionStatus.LoginRequired;
  const showDisconnect = connection.status !== BankConnectionStatus.Disconnected;
  const showRelink = connection.status === BankConnectionStatus.Disconnected;
  // Sync now only makes sense on a live connection: a LoginRequired Item must reconnect
  // first, and a Disconnected one is terminal. It's the free `/transactions/sync` cached
  // pull — never a billable live refresh.
  const showSync = connection.status === BankConnectionStatus.Active;
  const displayName = connection.institutionName || 'Bank';

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-4 p-4">
        <div className="flex items-center gap-3 min-w-0">
          <Landmark className="h-6 w-6 flex-shrink-0 text-muted-foreground" aria-hidden />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium text-gray-900">{displayName}</span>
              <BankConnectionStatusBadge status={connection.status} />
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Last synced: {formatRelativeTime(connection.lastSyncedAt)}
            </div>
          </div>
        </div>

        {showDisconnect && (
          <div className="flex flex-shrink-0 items-center gap-2">
            {/* Sync now — the free cached pull (`/transactions/sync`), so a treasurer about
                to file can grab the latest transactions the bank has already shared without
                waiting for the daily poll. Only on a live (Active) connection. */}
            {showSync && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onSync}
                disabled={isSyncing}
                className="gap-1.5"
              >
                {isSyncing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Sync now
              </Button>
            )}

            {/* Destructive-at-rest (red, not neutral-muted) so it never reads as the
                repairing Reconnect — a rushed treasurer must not kill a live connection
                by mistake. The confirm dialog is the second guard. */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onDisconnect}
              disabled={isDisconnecting}
              className="gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              {isDisconnecting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Disconnect
            </Button>
          </div>
        )}
      </div>

      {/* Plain-terms reassurance that Sync now is the free cached pull, not a live
          (billable) bank fetch — so a treasurer never fears clicking it runs up a cost. */}
      {showSync && (
        <p className="mx-4 mb-3 -mt-1 text-xs text-muted-foreground">
          Pulls the latest transactions your bank has already shared — no live fetch, always
          free.
        </p>
      )}

      {/* LoginRequired — the "needs re-authentication" prompt (AC): explains why
          new transactions stopped and offers the update-mode reconnect. The action
          names the bank so a treasurer with several rows knows which one it heals. */}
      {showReconnect && (
        <div className="mx-4 mb-4 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-amber-900">Reconnect to resume syncing</p>
            <p className="mt-0.5 text-xs text-amber-800">
              New transactions stopped syncing because {displayName} needs you to sign in
              again. Reconnect to pick up where syncing left off — your tracked accounts stay
              selected.
            </p>
            <Button
              size="sm"
              onClick={onReconnect}
              disabled={isReconnecting}
              className="mt-2 gap-1.5"
            >
              {isReconnecting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Reconnect {displayName}
            </Button>
          </div>
        </div>
      )}

      {/* Disconnected — a terminal state (ADR-070): the Plaid Item is removed, so
          reconnect can't heal it. The copy says the state is permanent and offers the
          only recovery, a fresh Re-link. The duplicate warning fires at click-time in
          the parent (ADR-075), not here. */}
      {showRelink && (
        <div className="mx-4 mb-4 flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5">
          <Unplug className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">This connection was removed</p>
            <p className="mt-0.5 text-xs text-gray-600">
              Disconnecting {displayName} is permanent — it can&apos;t be reconnected. To
              resume automatic syncing, re-link it as a fresh connection.
            </p>
            <Button
              size="sm"
              onClick={onRelink}
              disabled={isRelinking}
              className="mt-2 gap-1.5"
            >
              {isRelinking ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Re-link {displayName}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BankConnectionItem;
